import { usePropertyBuilder } from "@/components/views/config/helpers";
import type { IUseMods } from "@/components/views/mods";
import { useSteamWorkshop } from "@/components/views/mods/steam_workshop";
import type { HytaleProfileNamespace, SteamWorkshopItem } from "@/graphql";
import { i18n } from "@/i18n";
import { useServiceStore } from "@/store/service";
import { faPuzzlePiece } from "@fortawesome/pro-solid-svg-icons";
import { computed } from "vue";
import { commonMenuItems } from "./common";
import defaultProfile, { asyncRoute } from "./default";
import type { ConfigSection, GameProfile, Menu, ProfileCtx } from "./types";
import { MenuItem } from "./types";

const { t } = i18n.global;

const consoleCommands = [
  "/op",
  "help",
  "who",
  "worldsettings",
  "entity",
  "npc",
  "worldmap",
  "time",
  "update",
  "worldgen",
  "tp",
  "world",
];

const createMenu = (ctx: ProfileCtx): Menu => {
  const menu = defaultProfile(ctx).menu;
  menu[0].items.push(commonMenuItems.logs);
  menu[0].items.push(commonMenuItems.console);
  menu[0].items.push(
    new MenuItem(
      "mods",
      faPuzzlePiece,
      asyncRoute(() => import("../components/views/config/hytale/Mods.vue")),
      [
        {
          route: "manage",
          component: asyncRoute(() => import("../components/views/mods/manage/ModsManage.vue")),
        },
      ],
    ),
  );
  return menu;
};

const configSections: ConfigSection[] = [
  { component: "ConfSection", props: { name: "branch" } },
  { component: "ConfSection", props: { name: "basic" } },
  { component: "TableSection", props: { name: "broadcastMessages" } },
  { component: "ConfSection", props: { name: "extended" } },
  { component: "HytaleWorldSettings", props: { name: "worldSettings" } },
  { component: "HytaleBans" },
  { component: "HytalePermissions", props: { name: "permissions" } },
];

const join = () => {
  window.open(t("ngp/settings/section/hytale_wiki/link"), "_blank", "noopener,noreferrer");
};

export function useMods() {
  const service = useServiceStore();
  const { buildProperty } = usePropertyBuilder();
  const { buildConfig } = usePropertyBuilder();

  const profile = computed(() => service.ctx.profile as HytaleProfileNamespace | undefined);
  const selectedWorkshopItems = computed(() => profile.value?.selectedWorkshopItems ?? []);
  const subscribedFileIds = buildProperty<Array<string>>("virtual", "mods", []);
  const subscribedCollectionIds = buildConfig<Array<string>>("subscribedCollectionIds", []);
  const modPinnedVersions = buildProperty<Record<string, string>>("virtual", "mod_pinned_versions", {});

  const steamWorkshop = useSteamWorkshop(
    selectedWorkshopItems,
    subscribedFileIds,
    subscribedCollectionIds,
    "",
    false,
    true,
  );

  function setPinnedVersion(modId: string, repoKey: string | undefined) {
    const current = { ...modPinnedVersions.value };
    if (repoKey === undefined) {
      delete current[modId];
    } else {
      current[modId] = repoKey;
    }
    modPinnedVersions.value = current;
  }

  return { ...steamWorkshop, modPinnedVersions, setPinnedVersion };
}

export default (_ctx: ProfileCtx): Partial<GameProfile<SteamWorkshopItem, IUseMods<SteamWorkshopItem>>> => ({
  menu: createMenu(_ctx),
  configSections,
  consoleCommands,
  usesRcon: false,
  hasActivePlayers: true,
  useMods,
  join: {
    buttonTextKey: "ngp/settings/section/hytale_wiki",
    joinFn: join,
  },
});
